<script setup>
import { computed, onMounted, ref } from 'vue';
import { CATEGORY_METRICS } from '../constants';
import { useToggle } from '@vueuse/core';

import MetricCard from './overview/MetricCard.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const teams = useMapGetter('teams/getTeams');
const store = useStore();

const categoryi18nKey = 'OVERVIEW_REPORTS.CATEGORY_METRICS';

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const categoryMetrics = computed(() => {
  const data = accountConversationMetric.value || {};
  return {
    // conversations in the hands of a human agent (assignee set), computed by the backend
    agent_handled:
      data.agent_handled ?? (data.open || 0) - (data.unassigned || 0),
    // waiting for an agent after the bot handed off (open, unassigned, had a bot handoff)
    queued: data.queued ?? data.unassigned ?? 0,
    bot_handled: data.pending || 0,
    waiting_customer: data.snoozed || 0,
  };
});

const categoryMetricsDisplay = computed(() => {
  const metric = {};
  Object.keys(categoryMetrics.value).forEach(key => {
    const metricName = t(`${categoryi18nKey}.${CATEGORY_METRICS[key]}`);
    metric[metricName] = categoryMetrics.value[key];
  });
  return metric;
});

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});

const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="w-full">
    <MetricCard
      :header="t(`${categoryi18nKey}.HEADER`)"
      :is-loading="uiFlags.isFetchingAccountConversationMetric"
      :loading-message="t(`${categoryi18nKey}.LOADING_MESSAGE`)"
    >
      <template v-if="teams.length" #control>
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="relative flex items-center group z-50"
        >
          <Button
            sm
            slate
            faded
            :label="selectedTeamLabel"
            class="capitalize rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showDropdown"
            :menu-items="teamMenuList"
            class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
            label-class="capitalize"
            @action="handleAction($event)"
          />
        </div>
      </template>
      <div
        v-for="(metric, name, index) in categoryMetricsDisplay"
        :key="index"
        class="flex flex-col flex-1 min-w-0 pb-2"
      >
        <h3 class="text-xs text-n-slate-11 break-words">
          {{ name }}
        </h3>
        <p class="text-n-slate-12 text-2xl mb-0 mt-auto pt-1">
          {{ metric }}
        </p>
      </div>
    </MetricCard>
  </div>
</template>
